import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Tabs, Typography, message } from 'antd'
import { get } from '../../lib/request'
import { BenefitPlan, BusinessFormat, RewardType, errMsg } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'
import TypeFormatTab from './TypeFormatTab'
import PlanTab from './PlanTab'
import BenefitContentTab from './BenefitContentTab'
import { isFallbackPlan } from './shared'

const { Text } = Typography

/**
 * 答谢方案（thankyou_v2 批次 8.2，原「答谢配置」）。
 *
 * 设计契约：planning/design-assets-v2/admin-benefit-config-two-level.html
 * - 「答谢方案」tab = **两级列表**：左栏答谢类型（GET /admin/benefit-types，每类型显示兜底计数），
 *   右栏该类型下的方案表（GET /admin/benefit-plans 按 rewardTypeId 前端过滤，include tiers）
 * - 左栏底部「+ 新建类型（需资质校验勾选）」→ 切到「答谢类型」tab（新建弹窗含 needQualification Checkbox）
 * - 类型 / 业态管理仍由 TypeFormatTab 承载（二级 Tab 入口，软删语义与内置行保护不变）
 *
 * 权限：路由本身已限 super_admin / admin（App.tsx RoleRoute），故页内读列表不再二次限读；
 * 写操作 gate = hasPermission(role, 'BUSINESS_CONFIG_EDIT')。
 * 三类对象均**无物理删除**：「删除」语义 = 停用（PATCH { status: 'offline' }，软删）。
 */
const BenefitConfigPage: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = hasPermission(role, 'BUSINESS_CONFIG_EDIT')

  const [types, setTypes] = useState<RewardType[]>([])
  const [formats, setFormats] = useState<BusinessFormat[]>([])
  const [plans, setPlans] = useState<BenefitPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(false)
  /** 左栏选中的答谢类型（null = 全部类型） */
  const [activeTypeId, setActiveTypeId] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState('plans')

  const fetchOptions = useCallback(() => {
    // 方案表单的下拉选项来源；任一 tab 变更后同步刷新（onChanged）
    get<RewardType[]>('/admin/benefit-types')
      .then((d) => setTypes(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '答谢类型加载失败')))
    get<BusinessFormat[]>('/admin/business-formats')
      .then((d) => setFormats(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '业态加载失败')))
  }, [])

  const fetchPlans = useCallback(() => {
    setPlansLoading(true)
    // ST1 起 GET /admin/benefit-plans include tiers（分档子表随方案行返回）
    get<BenefitPlan[]>('/admin/benefit-plans')
      .then((d) => setPlans(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '答谢方案加载失败')))
      .finally(() => setPlansLoading(false))
  }, [])

  useEffect(() => { fetchOptions(); fetchPlans() }, [fetchOptions, fetchPlans])

  /** 任一写操作后：类型/业态与方案一并刷新（左栏兜底计数依赖方案列表） */
  const refreshAll = useCallback(() => { fetchOptions(); fetchPlans() }, [fetchOptions, fetchPlans])

  /** 每类型兜底计数：effectiveTo 为 null 且 published（Contract Pack 兜底定义） */
  const fallbackCountByType = useMemo(() => {
    const map: Record<number, number> = {}
    plans.forEach((p) => {
      if (isFallbackPlan(p)) map[p.rewardTypeId] = (map[p.rewardTypeId] ?? 0) + 1
    })
    return map
  }, [plans])

  // 左栏类型项：已发布优先、按 sortOrder（后端已排序，此处保持原序）
  const typeListItems = useMemo(
    () => types.filter((t) => t.status === 'published'),
    [types],
  )

  const leftColumn = (
    <div
      style={{
        width: 210,
        flexShrink: 0,
        background: '#fff',
        border: '1px solid #E8ECF0',
        borderRadius: 8,
        padding: 10,
        alignSelf: 'flex-start',
      }}
    >
      <Text strong style={{ display: 'block', marginBottom: 8 }}>答谢类型</Text>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setActiveTypeId(null)}
        onKeyDown={(e) => { if (e.key === 'Enter') setActiveTypeId(null) }}
        style={{
          padding: '8px 10px',
          marginBottom: 4,
          borderRadius: 6,
          cursor: 'pointer',
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          ...(activeTypeId == null
            ? { background: '#EAF5ED', borderLeft: '3px solid #1A873B', fontWeight: 600 }
            : { borderLeft: '3px solid transparent' }),
        }}
      >
        全部类型（{plans.length}）
      </div>
      {typeListItems.map((t) => {
        const active = activeTypeId === t.id
        const fb = fallbackCountByType[t.id] ?? 0
        return (
          <div
            key={t.id}
            role="button"
            tabIndex={0}
            onClick={() => setActiveTypeId(t.id)}
            onKeyDown={(e) => { if (e.key === 'Enter') setActiveTypeId(t.id) }}
            style={{
              padding: '8px 10px',
              marginBottom: 4,
              borderRadius: 6,
              cursor: 'pointer',
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 6,
              ...(active
                ? { background: '#EAF5ED', borderLeft: '3px solid #1A873B', fontWeight: 600 }
                : { borderLeft: '3px solid transparent' }),
            }}
          >
            <span>{t.name}</span>
            {/* 兜底计数：绿底浅标；0 时红字提醒该类型尚无兜底方案 */}
            <Text
              style={{
                fontSize: 12,
                color: fb > 0 ? '#1A873B' : '#E74C3C',
                whiteSpace: 'nowrap',
              }}
            >
              兜底 {fb}
            </Text>
          </div>
        )
      })}
      {canEdit && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setActiveTab('types')}
          onKeyDown={(e) => { if (e.key === 'Enter') setActiveTab('types') }}
          style={{ color: '#888', padding: '8px 10px', cursor: 'pointer', minHeight: 44 }}
        >
          + 新建类型（需资质校验勾选）
        </div>
      )}
    </div>
  )

  return (
    <>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        两级列表：左侧选答谢类型，右侧管理该类型下的方案（新增 / 编辑 / 复制 / 停用）。
        每类型有且仅有一个「生效止留空 + 已发布」的兜底方案，不可停用、不可改生效期；
        类型 / 业态 / 方案均无物理删除，「停用」即软删下架（内置类型与内置三业态不可停用）。
      </Text>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'plans',
            label: '答谢方案',
            children: (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {leftColumn}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <PlanTab
                    canEdit={canEdit}
                    rewardTypeId={activeTypeId}
                    types={types}
                    formats={formats}
                    plans={plans}
                    loading={plansLoading}
                    onChanged={refreshAll}
                  />
                </div>
              </div>
            ),
          },
          {
            key: 'types',
            label: '答谢类型',
            children: (
              <TypeFormatTab kind="type" canEdit={canEdit} onChanged={refreshAll} />
            ),
          },
          {
            key: 'formats',
            label: '业态',
            children: (
              <TypeFormatTab kind="format" canEdit={canEdit} onChanged={refreshAll} />
            ),
          },
          {
            // benefit_editable_v1（2026-09-14）：被推荐人礼遇内容编辑（中/英标题+介绍+长图）。
            // 编辑入口位置 = 「业态」后面（用户指定）；写操作 gate 与其它 tab 同为 canEdit。
            key: 'content',
            label: '被推荐人礼遇',
            children: (
              <BenefitContentTab canEdit={canEdit} onChanged={refreshAll} />
            ),
          },
        ]}
      />
    </>
  )
}

export default BenefitConfigPage
